import { useMemo } from 'react';

import { useAssetMetersQuery } from '../queries/assets';
import { useAppSelector } from '../store/hooks';
import { MeterMeasurement } from '../types/asset';
import { MetadataOptionsQuestion, NumberQuestionBase } from '../types/questions';

/**
 * Returns the last measurement for the current number question when it is linked
 * to an "assets" select_metadata question via meterConfigurations and an asset is selected.
 * Only applies when the associated question uses metadata_code === 'assets' and has meter configs.
 */
export function useAssetLastMeasurement(question: NumberQuestionBase): MeterMeasurement | null {
  const questions = useAppSelector((state) => state.webForms.questions);
  const answers = useAppSelector((state) => state.webForms.answers);
  const metadataMap = useAppSelector((state) => state.webForms.metadata);

  const { meterId, selectedAssetId } = useMemo(() => {
    if (question.type !== 'number') return { meterId: null, selectedAssetId: null };

    const meta = (question as NumberQuestionBase).associatedMetadata;
    const questionId = meta?.questionId;
    const meterIdStr = meta?.meterId;
    if (questionId == null || meterIdStr == null) return { meterId: null, selectedAssetId: null };

    const assetsQuestion = questions.find(
      (candidateQuestion) => candidateQuestion.id === String(questionId),
    ) as MetadataOptionsQuestion | undefined;
    if (
      assetsQuestion?.type !== 'select_metadata' ||
      assetsQuestion.metadata_code !== 'assets' ||
      !assetsQuestion.meterConfigurations?.length
    ) {
      return { meterId: null, selectedAssetId: null };
    }

    const meterConfig = assetsQuestion.meterConfigurations.find(
      (config) => !config.isLocation && config.associatedQuestions?.includes(question.id),
    );
    if (!meterConfig || String(meterConfig.meterId) !== meterIdStr)
      return { meterId: null, selectedAssetId: null };

    const assetAnswer = answers[assetsQuestion.id]?.value;
    if (typeof assetAnswer !== 'string' || !assetAnswer)
      return { meterId: meterIdStr, selectedAssetId: null };

    const assets = metadataMap['assets'] as { id: number; name: string }[] | undefined;
    const asset = assets?.find((entry) => entry.name === assetAnswer);
    const assetId = asset?.id ?? null;

    return { meterId: meterIdStr, selectedAssetId: assetId };
  }, [question, questions, answers, metadataMap]);

  const assetIdForMeters = selectedAssetId != null && meterId != null ? selectedAssetId : null;
  const { data: meters } = useAssetMetersQuery(assetIdForMeters, false);

  const lastMeasurement = useMemo(() => {
    if (!meters || !Array.isArray(meters) || meterId == null) return null;
    const meter = meters.find((meterEntry) => String(meterEntry.meter_id) === String(meterId));
    return meter?.last_measurement ?? null;
  }, [meters, meterId]);

  return lastMeasurement;
}
